import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import { useBookingDialog } from "@/context/BookingDialogContext";
import { api } from "@/lib/api";
import { eur } from "@/lib/constants";
import { FacilityCard } from "@/components/FacilityCard";
import { BookingRow } from "@/components/BookingRow";
import { LogItem } from "@/components/LogItem";

const Kpi = ({ label, value, hint, testId, accent }) => (
  <div data-testid={testId} className={`rounded-xl border p-6 ${accent ? "border-[#8B1E2B] bg-[#8B1E2B] text-white" : "border-slate-200 bg-white"}`}>
    <p className={`eyebrow ${accent ? "!text-rose-100" : ""}`}>{label}</p>
    <p className="mt-3 font-mono text-3xl font-medium tracking-tight">{value}</p>
    <p className={`mt-1 text-xs ${accent ? "text-rose-100" : "text-slate-500"}`}>{hint}</p>
  </div>
);

const Panel = ({ title, link, linkLabel, children, testId }) => (
  <section data-testid={testId} className="rounded-xl border border-slate-200 bg-white">
    <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
      <h2 className="font-serif text-2xl font-semibold text-slate-900">{title}</h2>
      {link && <Link to={link} className="text-sm font-semibold text-[#8B1E2B] hover:underline">{linkLabel}</Link>}
    </header>
    {children}
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { version } = useBookingDialog();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, [version]);

  if (!d) return <p className="eyebrow" data-testid="dashboard-loading">Caricamento…</p>;
  const names = Object.fromEntries(d.facilities.map((f) => [f.slug, f.name]));
  const operative = d.facilities.filter((f) => f.status === "disponibile").length;

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      <div className="fade-up">
        <p className="eyebrow">{format(new Date(), "EEEE d MMMM yyyy", { locale: it })}</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">Buongiorno, {user.name.split(" ")[0]}</h1>
        <p className="mt-2 text-slate-600">Stato delle strutture comunali e delle concessioni in corso.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="kpi-due" accent label="Da incassare" value={eur(d.due_total)} hint={`${d.due_count} pratiche in attesa di pagamento`} />
        <Kpi testId="kpi-paid" label={`Incassato ${d.today.slice(0, 4)}`} value={eur(d.paid_year)} hint={`${d.paid_count} pratiche pagate`} />
        <Kpi testId="kpi-next30" label="Prossimi 30 giorni" value={d.next30_count} hint="eventi in calendario" />
        <Kpi testId="kpi-operative" label="Strutture operative" value={`${operative}/${d.facilities.length}`} hint="disponibili ad oggi" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {d.facilities.map((f, i) => <FacilityCard key={f.slug} facility={f} index={i} upcoming={d.upcoming_by_facility[f.slug] || 0} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <Panel testId="upcoming-panel" title="Prossimi eventi" link="/calendario" linkLabel="Apri calendario">
          {d.upcoming.length === 0
            ? <p className="px-5 py-10 text-center text-sm text-slate-500">Nessun evento in programma.</p>
            : <div className="divide-y divide-slate-100">{d.upcoming.map((b) => <BookingRow key={b.id} b={b} facilityName={names[b.facility_slug]} />)}</div>}
        </Panel>
        <Panel testId="logs-panel" title="Registro struttura" link="/strutture" linkLabel="Tutte le strutture">
          {d.recent_logs.length === 0
            ? <p className="px-5 py-10 text-center text-sm text-slate-500">Nessuna nota o segnalazione.</p>
            : <ul className="divide-y divide-slate-100">{d.recent_logs.map((l) => <LogItem key={l.id} log={l} facilityName={names[l.facility_slug]} />)}</ul>}
        </Panel>
      </div>
    </div>
  );
}
